import { useState, useEffect } from 'react';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { TeamMember, TrainingCamp } from '../lib/types';
import { TeamMemberCard } from '../components/Team/TeamMemberCard';
import { TrainingCampModal } from '../components/Team/TrainingCampModal';
import { CompetitionsSection } from '../components/Team/CompetitionsSection';
import { PricingSection } from '../components/Team/PricingSection';
import { SkillLevelsSection } from '../components/Team/SkillLevelsSection';
import { FAQSection } from '../components/Team/FAQSection';
import { VideoGallery } from '../components/Team/VideoGallery';

export function Team() {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [showAllMembers, setShowAllMembers] = useState(false);
  const [selectedCamp, setSelectedCamp] = useState<TrainingCamp | null>(null);
  const [camps, setCamps] = useState<TrainingCamp[]>([]);

  useEffect(() => {
    const fetchMembers = async () => {
      const membersRef = collection(db, 'team');
      const q = query(membersRef, orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      const membersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as TeamMember[];
      setMembers(membersData);
    };

    const fetchCamps = async () => {
      const campsRef = collection(db, 'trainingCamps');
      const q = query(campsRef, orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      const campsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as TrainingCamp[];
      setCamps(campsData);
    };

    fetchMembers();
    fetchCamps();
  }, []);

  const displayedMembers = showAllMembers ? members : members.slice(0, 4);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center">
        <img
          src="https://storage.googleapis.com/nhvelo-f0124.appspot.com/images/1735895036171.jpg"
          alt="NH Velo Team"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">NH Velo Racing Team</h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Pasionați de mountain biking, dedicați performanței și dezvoltării comunității
          </p>
        </div>
      </section>

      {/* Team Members Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Echipa Noastră</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {displayedMembers.map(member => (
              <TeamMemberCard key={member.id} member={member} />
            ))}
          </div>
          {members.length > 4 && (
            <div className="text-center mt-8">
              <button
                onClick={() => setShowAllMembers(!showAllMembers)}
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                {showAllMembers ? 'Vezi mai puțin' : 'Vezi toți riderii'}
                <ChevronDown className={`w-5 h-5 transition-transform ${showAllMembers ? 'rotate-180' : ''}`} />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Competitions Section */}
      <CompetitionsSection />

      {/* Pricing Section */}

      {/* Skill Levels Section */}

      {/* FAQ Section */}
      <FAQSection />

      {/* Video Gallery */}
      <VideoGallery />

      {/* Join the Team Section */}
      <section className="bg-orange-500 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-white">
            <h2 className="text-4xl font-bold mb-4">Vrei să faci parte din echipă?</h2>
            <p className="text-xl mb-8">
              Te invităm să participi la testul nostru de aptitudini pentru a te alătura echipei NH Velo. Descoperă-ți potențialul și începe aventura alături de noi!
            </p>
            <a 
              href="#"
              className="inline-flex items-center gap-2 bg-white text-orange-500 px-8 py-4 rounded-lg font-semibold hover:bg-orange-50 transition-colors"
            >
              Înscrie-te acum
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Training Camps Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4">Training Camps</h2>
          <p className="text-xl text-gray-600 text-center mb-12">
            Pregătire intensivă în locații premium pentru toate nivelurile
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {camps.map((camp) => (
              <div
                key={camp.id}
                className="bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer transform hover:scale-105 transition-transform duration-300"
                onClick={() => setSelectedCamp(camp)}
              >
                <div className="relative">
                  <img
                    src={camp.imageUrl}
                    alt={camp.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full font-semibold shadow">
                    {camp.price} €
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2">{camp.title}</h3>
                  <p className="text-gray-600 mb-4">{camp.shortDescription}</p>
                  
                  <div className="flex flex-wrap gap-2">
                    {camp.categories.map((category, index) => (
                      <span
                        key={index}
                        className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm"
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {selectedCamp && (
        <TrainingCampModal
          camp={selectedCamp}
          onClose={() => setSelectedCamp(null)}
        />
      )}
    </div>
  );
}

export default Team;